import React, {FC, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {
    Alert,
    Button, Modal,
    ModalBody as Body,
    ModalFooter as Footer,
    ModalHeader as Header,
    ModalTitle as Title
} from "react-bootstrap";
import GroupedAction from "Entity/Collection/ListingSetting/GroupedAction";
import LoadingButton from "../../Button/LoadingButton";
import Collection from "Config/Collection";
import Property from "../../../Entity/Property";
import {useNavigate} from "react-router-dom";
import FormEmpty from "../../../Vendor/Components/FormEmpty";
import {classUtils as c} from "../../../Vendor/Utils/ClassUtils";
import Api from "Vendor/Api";
import {ShareLock, useWindowId} from "Hooks/useShareEditingTick";

export interface _BimboShareModalProps {
    collection?: Collection
    showActionModals: {[key: string]: boolean|Property}
    handleShowActionModal: (groupedAction?: GroupedAction, actionFromMain?:boolean) => void
    fromMain: boolean
    handleShowMainActionModal: () => void
    selected: number[]
    actionModalFormIsLoading: boolean
    handleActionModalOnResponse: () => void
    handleActionModalOnSuccess: () => void
    handleActionModalOnSubmit: () => void
    actionModalFormIsLoaded: boolean,
    setActionModalFormIsLoaded: (isLoaded: boolean) => void,
}

export type BimboShareActionModal = undefined | 'new' | 'addItems'

const _BimboShareModal: FC<_BimboShareModalProps> = ({
                                                   collection,
                                                   handleShowActionModal,
                                                   fromMain,
                                                   handleShowMainActionModal,
                                                   selected,
                                                   actionModalFormIsLoading,
                                                   setActionModalFormIsLoaded,
                                                   handleActionModalOnResponse,
                                                   handleActionModalOnSuccess,
                                                   handleActionModalOnSubmit,
                                                   actionModalFormIsLoaded,
                                                   showActionModals,
                                               }) => {
    const {t} = useTranslation()
    const navigate = useNavigate()
    const [additionalData, setAdditionalData] = useState<undefined|object>(undefined)
    const [bimboShareActionType, setBimboShareActionType] = useState<BimboShareActionModal>(undefined)
    const [isShareLocked, setIsShareLocked] = useState<boolean>(false)
    const [isCheckingLock, setIsCheckingLock] = useState<boolean>(false)
    const modalBodyRef = useRef<HTMLDivElement>(null)
    const windowId = useWindowId()

    const onSubmit = (e?: React.FormEvent<HTMLFormElement>) => {
        if (isShareLocked) {
            if (e) {
                e.preventDefault()
                e.stopPropagation()
            }
            return false
        }
        handleActionModalOnSubmit()
    }

    useEffect(() => {
        if (collection === 'catalog') {
            setAdditionalData({
                products: selected,
            })
        } else if (collection === 'bucket') {
            setAdditionalData({
                files: selected,
            })
        } else {
            setAdditionalData(undefined)
        }
    }, [collection, selected])

    useEffect(() => {
        if (bimboShareActionType !== 'addItems' || !actionModalFormIsLoaded) {
            setIsShareLocked(false)
            setIsCheckingLock(false)
            return
        }

        const container = modalBodyRef.current
        if (!container) {
            return
        }

        const checkLockForSelect = () => {
            const selectEl = container.querySelector('select[name*="bimboShare"]') as HTMLSelectElement | null
            if (!selectEl || !selectEl.value) {
                setIsShareLocked(false)
                setIsCheckingLock(false)
                return
            }

            const selectedOption = selectEl.options[selectEl.selectedIndex]
            const uuid = selectedOption?.getAttribute('data-uuid')
            const identifier = uuid || selectEl.value

            if (!identifier) {
                setIsShareLocked(false)
                setIsCheckingLock(false)
                return
            }

            setIsCheckingLock(true)
            Api.get(`/bimbo-share/${identifier}/lock`, { params: { window: windowId } })
                .then(({ data }: { data: { lock?: ShareLock; locked?: boolean } }) => {
                    const isLocked = data?.locked ?? (data?.lock !== 'mine')
                    setIsShareLocked(isLocked)
                })
                .catch(() => {
                    setIsShareLocked(false)
                })
                .finally(() => {
                    setIsCheckingLock(false)
                })
        }

        const $container = $(container)
        $container.on('change select2:select select2:clear', 'select[name*="bimboShare"]', checkLockForSelect)

        checkLockForSelect()
        const timer = window.setTimeout(checkLockForSelect, 150)

        return () => {
            window.clearTimeout(timer)
            $container.off('change select2:select select2:clear', 'select[name*="bimboShare"]', checkLockForSelect)
        }
    }, [bimboShareActionType, actionModalFormIsLoaded, windowId])

    const onSuccess = (data: any) => {
        navigate('/bimbo-share/', {state: {
            bimboShare: data.uuid
        }})
    }

    const onHide = () => {
        handleShowActionModal()
        setBimboShareActionType(undefined)
        setIsShareLocked(false)
        setIsCheckingLock(false)
    }

    return <Modal show={showActionModals.share !== false} onHide={onHide}>
        <Header closeButton={!fromMain}>
            <div className="modal-block-header">
                {fromMain && <i className="fa-solid fa-angle-left" onClick={() => {
                    if (bimboShareActionType === undefined) {
                        handleShowMainActionModal()
                    } else {
                        setBimboShareActionType(undefined)
                        setIsShareLocked(false)
                        setIsCheckingLock(false)
                    }
                }}></i>}
                <Title>
                    {t(bimboShareActionType === undefined ? 'app.bimboShare' : `app.groupedActionChoice.bimboShare.${bimboShareActionType}`, {count: selected.length})}
                </Title>
            </div>
        </Header>
        <Body>
            {bimboShareActionType === undefined ?
                <ul className="grouped-action-choices">
                    <li onClick={() => {
                        setBimboShareActionType('new')
                    }} className={c("btn btn-secondary", selected.length === 0 && 'disabled')}>
                                <span>
                                    <i className="fa-solid fa-plus icon"></i>
                                    {t('app.groupedActionChoice.bimboShare.new', {count: selected.length})}
                                </span>
                        <i className="action-icon"></i>
                    </li>
                    <li onClick={() => {
                        setBimboShareActionType('addItems')
                    }} className={c("btn btn-secondary", selected.length === 0 && 'disabled')}>
                                <span>
                                    <i className="fa-solid fa-list-check icon"></i>
                                    {t('app.groupedActionChoice.bimboShare.addItems', {count: selected.length})}
                                </span>
                        <i className="action-icon"></i>
                    </li>
                </ul>
                :
                <div ref={modalBodyRef}>
                    {isShareLocked && (
                        <Alert variant="warning" className="bimbo-share-notice d-flex align-items-center mb-3">
                            <i className="fa-solid fa-lock me-2 text-warning fs-5"></i>
                            <div>
                                {t(collection === 'catalog'
                                    ? 'app.groupedActionChoice.bimboShare.lockedBannerProducts'
                                    : collection === 'bucket'
                                        ? 'app.groupedActionChoice.bimboShare.lockedBannerFiles'
                                        : 'app.groupedActionChoice.bimboShare.lockedBanner')}
                            </div>
                        </Alert>
                    )}
                    <FormEmpty
                        path={bimboShareActionType === 'new' ? '/bimbo-share/new/1' : `/bimbo-share/add-items`}
                        method='post'
                        additionalData={additionalData}
                        additionalParams={{ window: windowId }}
                        setIsLoaded={setActionModalFormIsLoaded}
                        onResponse={handleActionModalOnResponse}
                        onSubmit={onSubmit}
                        onSuccess={onSuccess}
                        onConflict={(data) => {
                            if (data?.reason === 'locked_by_other') {
                                setIsShareLocked(true)
                            }
                        }}
                    >
                        <div className="row justify-content-end align-items-center mb-3">
                            <div className="col-auto">
                                <LoadingButton
                                    type="submit"
                                    isLoading={actionModalFormIsLoading || isCheckingLock}
                                    disabled={!actionModalFormIsLoaded || isShareLocked || isCheckingLock}
                                >
                                    {t('app.add')}
                                </LoadingButton>
                            </div>
                        </div>
                    </FormEmpty>
                </div>
            }
        </Body>
        {!fromMain && <Footer>
            <Button variant="secondary" onClick={onHide}>
                {t('app.cancel')}
            </Button>
        </Footer>}
    </Modal>
}

export default _BimboShareModal
